import React from 'react';
import clsx from 'clsx';
import { Box, IconButton, Dialog, makeStyles } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    '& .title': {
      fontSize: 20,
      fontWeight: 700,
      [theme.breakpoints.down('sm')]: {
        fontSize: 16
      }
    },
    '& .close': {
      padding: 0
    }
  },
  headerBg: {
    backgroundColor: theme.palette2.background.modalHeader
  },
  headerBorder: {
    borderBottom: `1px solid ${theme.palette2.divider}`
  },
  footerBorder: {
    borderTop: `1px solid ${theme.palette2.divider}`
  },
  content: {
    maxHeight: 500,
    overflow: 'scroll',
    [theme.breakpoints.down('md')]: {
      maxHeight: 450
    }
  },
  footerShadow: {
    boxShadow: '0px 0px 44px rgba(51, 51, 51, 0.25)'
  }
}));

interface AppModalProps {
  open: boolean;
  onClose: () => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  disableEscapeKeyDown?: boolean;
  fullScreen?: boolean;
  fullWidth?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  onBackdropClick?: () => void;
  headerCentered?: boolean;
  headerBg?: boolean;
  headerBorder?: boolean;
  footerBorder?: boolean;
  footerShadow?: boolean;
  className?: string;
  id?: string;
}

const AppModal: React.FC<AppModalProps> = ({
  open,
  onClose,
  header,
  footer,
  children,
  disableEscapeKeyDown,
  fullScreen,
  fullWidth = true,
  maxWidth = 'sm',
  onBackdropClick,
  headerCentered = true,
  headerBg = true,
  footerShadow = true,
  className = '',
  headerBorder = false,
  footerBorder = false
}) => {
  const classes = useStyles();
  return (
    <Dialog
      onClose={onClose}
      open={open}
      disableEscapeKeyDown={disableEscapeKeyDown}
      fullScreen={fullScreen}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      onBackdropClick={onBackdropClick}
    >
      {header && (
        <Box
          className={clsx(
            classes.header,
            headerBg ? classes.headerBg : '',
            headerBorder ? classes.headerBorder : ''
          )}
          py={1.5}
          px={{ xs: 1.5, md: 2.25 }}
          textAlign="center"
          display="flex"
          justifyContent="center"
        >
          <Box ml={headerCentered ? 'auto' : ''}>{header}</Box>
          <Box ml="auto">
            <IconButton className="close" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      <Box className={clsx(classes.content, className)}>{children}</Box>

      {footer && (
        <Box
          className={clsx(
            footerShadow ? classes.footerShadow : '',
            footerBorder ? classes.footerBorder : ''
          )}
          py={1}
          px={{ xs: 2, md: 3 }}
        >
          {footer}
        </Box>
      )}
    </Dialog>
  );
};

export default AppModal;
